/**
 * ═══════════════════════════════════════════════════════════════════════════
 * USER VIEWED PAGE - VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CASCADE STRICTE 3 niveaux:
 *   NIVEAU 1 — allSettings.pages.userView.sections[]  (DB AnimationSettings)
 *   NIVEAU 2 — allSettings.pages.userView.facets[]    (DB AnimationSettings)
 *   NIVEAU 3 — PAGES_CONFIG_.userView                 (statique pagesConfig.js)
 *
 * userViewGrid → max 36 produits, PAS de pagination next/prev
 * (skip fixe à 0, limit fixe à 36 → tous les produits vus en une seule page)
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Layout from '../Layout/Layout';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
} from '../pageInside/exeemple_page/pagesConfig';
import { isAuthenticated } from '../auth';
import { prices } from '../pageInside/fixedPrices';
import { getPaginationLimit } from '../../utils/paginationSettings';


// ═══════════════════════════════════════════════════════════════════════════
// PAGE ID
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID  = 'userView';
const SKIP_MAX = 0;   // toujours 0 — pas de pagination pour userViewGrid
const LIMIT_MAX = 36; // max produits vus affichés

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.userView
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return ['userViewGrid', 'userViewMatrix', 'historySearchMatrix', 'followMe'];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) {
            result[s.sectionId] = entry;
        }
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2 (DB AnimationSettings)
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // NIVEAU 1 — sections[] DB
    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // NIVEAU 2 — facets[] DB
    if (Array.isArray(facets) && facets.length > 0) {
        return { facets, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig — CASCADE 3 niveaux
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const fromDB = _extractFromSettings(pageSettings);
    if (fromDB) return fromDB;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT
// ═══════════════════════════════════════════════════════════════════════════

const UserVieweds = (props) => {
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
        nbrArr,
    } = props;

    const { token } = isAuthenticated();

    // ─── Filtres ──────────────────────────────────────────────────────────────
    const [myFilters, setMyFilters] = useState({ filters: {} });

    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => ({
            ...prev,
            filters: { ...prev.filters, [filterBy]: filters },
        }));
    }, []);

    // ─── Ref scroll ───────────────────────────────────────────────────────────
    const divTop = useRef(null);

    // ─── CASCADE ──────────────────────────────────────────────────────────────
    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [pageSettings]
    );

    // Admin-configurable via AnimationSettings → General → "Pagination &
    // Limites" (paginationLimits.userView) — falls back to LIMIT_MAX (36).
    const effectiveLimit = getPaginationLimit(infoSource?.allSettings, 'userView', LIMIT_MAX);

    // ─── DataLoader ───────────────────────────────────────────────────────────
    // skip=0 et limit=36 fixes → charge tous les produits vus en une seule requête
    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        skip:    SKIP_MAX,       // 0 — fixe, pas de pagination
        limit:   effectiveLimit, // 36 par défaut — max produits vus
        filters: myFilters.filters,
    });

    // ─── Sync infoSource → pageData (loaderType NONE) ────────────────────────
    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) mergePageData(updates);
    }, [infoSource, facets]);

    // ─── Recharger quand facets / filtres changent ────────────────────────────
    // PAS de skip dans les deps → skip est fixe à 0
    const facetsKey  = useMemo(() => facets.join(','),                  [facets]);
    const filtersKey = useMemo(() => JSON.stringify(myFilters.filters), [myFilters.filters]);

    useEffect(() => {
        loadPageData();
    }, [facetsKey, filtersKey]);



    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Layout
            infoSource={infoSource}
            loading={loading}
            prices={prices}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            SliderPunShow={false}
            P1={(pageData?.size || 0) > 0}
            {...infoSource?.allSettings?.layout}
        >
            <div  style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    page="userView"
                    totalKey="userViewTotal"
                    myFilters={myFilters}
                    nbrArr={nbrArr}
                    reloadFacets={reloadFacets}
                />

                {loading && !pageData?.userViewedProduct?.length && (
                    <div className="row justify-content-center mt-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).userViewed.t001}</span>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default UserVieweds;
